import { useCallback, useState } from "react";
import { Alert, Button, Card } from "react-bootstrap";
import { useDropzone } from "react-dropzone";
import { FormattedMessage } from "react-intl";
import { Link } from "react-router-dom";
import styled from "styled-components";
import { SectionHeading } from "../../components/section-heading";
import { messages } from "./TagsMultiUpdate.messages";
import { useProperty } from "../../components/property-provider";
import { useTagsContext } from "./TagContext";
import { TagMultiUpdateResponse } from "./domain/TagMultiUpdateResponse";
import { TagMultiUpdateResult } from "./TagMultiUpdateResult";
import { FullWidthLoader } from "../../components/full-width-loader";

const Container = styled.div`
  display: flex;
  flex-direction: column;
  padding: 2rem 1rem;
  gap: 2rem;
`;

const CardContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: 2rem 1rem;
  gap: 2rem;
`;

const ButtonsContainer = styled.div`
  display: flex;
  gap: 1rem;
  align-items: center;
`;

const DropzoneContainer = styled.div`
  height: 100px;
  border: 2px dashed #dae6e8;
  background-color: ${({ theme }) => theme.greyBackground};;
  font-size: 21px;

  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  outline: none;
`;

const ResultsContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

export function TagsMultiUpdate() {
  const [file, setFile] = useState<File | undefined>(undefined);
  const [importResult, setImportResult] = useState<TagMultiUpdateResponse | undefined>(undefined);
  const [isLoading, setLoading] = useState(false);
  const { ticker: propertyTicker } = useProperty();
  const { importTags } = useTagsContext();

  const onDrop = useCallback(<T extends File>(acceptedFiles: T[]) => {
    if (!acceptedFiles || acceptedFiles.length === 0) {
      return;
    }
    setFile(acceptedFiles[0]);
    setImportResult(undefined);
  }, []);

  const { getRootProps, getInputProps } = useDropzone({ onDrop });

  async function onImport() {
    if (!file) return;

    try {
      setLoading(true);
      setImportResult(await importTags(propertyTicker, file));
    } catch (e) {
      console.error("Some error occur during tags import", e);
    } finally {
      setFile(undefined);
      setLoading(false);
    }
  }

  if (isLoading) {
    return <FullWidthLoader />;
  }

  return (
    <>
      <SectionHeading>
        <FormattedMessage {...messages.title} />
      </SectionHeading>
      <Container>
        <Card>
          <CardContainer>
            <FormattedMessage {...messages.instructions} />
            <DropzoneContainer {...getRootProps()}>
              <input {...getInputProps()} />
              {file ? <span>{file.name}</span> : <FormattedMessage {...messages.dropFiles} />}
            </DropzoneContainer>
            <ButtonsContainer>
              <Button type="primary" onClick={onImport}>
                <FormattedMessage {...messages.btnImportFile} />
              </Button>
              <a
                className="btn btn-primary"
                type="primary"
                href={`/v6/api/v1/${propertyTicker}/tags/export`}>
                <FormattedMessage {...messages.btnDownloadFile} />
              </a>
            </ButtonsContainer>
            {importResult && (
              <ResultsContainer>
                <Alert variant="success">
                  <strong>
                    <FormattedMessage {...messages.saveSuccess} />
                  </strong>
                </Alert>
                <TagMultiUpdateResult result={importResult} />
              </ResultsContainer>
            )}
          </CardContainer>
        </Card>
        <ButtonsContainer className="justify-center">
          <Link className="btn btn-primary" to="tags">
            <FormattedMessage {...messages.btnBackToTags} />
          </Link>
        </ButtonsContainer>
      </Container>
    </>
  );
}
